import { useCallback, useRef } from 'react';
import arrayMutators from 'final-form-arrays';
import { Form, FormRenderProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

import { IKnmRejectReasonType } from 'APIPackage/CatalogsModule/interfaces/IKnmRejectReasonType';
import { useKnmRejectReasonTypes } from 'CorePackage/CatalogsModule/hooks/useKnmRejectReasonTypes';
import { IKnmRejectReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmRejectReasonTypeSelectOption';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import styles from './DisapproveKnmsModal.module.scss';

interface IDisapproveRejectReason {
  type: IKnmRejectReasonType;
  note?: string;
}

interface IDisapproveKnmsFormRejectReason {
  type?: IKnmRejectReasonTypeSelectOption;
  note?: string;
}

interface IDisapproveKnmsForm {
  rejectReasons?: Array<IDisapproveKnmsFormRejectReason | undefined>;
}

type IDisapproveKnmsFormRenderProps = FormRenderProps<IDisapproveKnmsForm>;

export interface IDisapproveKnmsModalProps {
  visible?: boolean;
  knmTypeId?: number;
  knmsNumber: number;
  onApply: (rejectReasons: IDisapproveRejectReason[]) => void;
  onClose: () => void;
}

export const DisapproveKnmsModal = ({
  visible,
  knmTypeId,
  knmsNumber,
  onApply,
  onClose,
}: IDisapproveKnmsModalProps) => {
  const formRef = useRef<IDisapproveKnmsFormRenderProps['form']>();
  const { knmRejectReasonTypesOptions } = useKnmRejectReasonTypes(visible ? knmTypeId : undefined);

  const handleApplyButtonClick = useCallback(() => formRef.current && formRef.current.submit(), []);

  const handleAfterClose = useCallback(() => formRef.current && formRef.current.reset(), []);

  const renderForm = useCallback(
    ({ form }: IDisapproveKnmsFormRenderProps) => {
      formRef.current = form;
      const { push, remove } = form.mutators;

      function addRejectReason() {
        push('rejectReasons', undefined);
      }

      const makeRemoveRejectReasonHandler = (index: number) => () => remove('rejectReasons', index);

      return (
        <form>
          <Row>
            <FieldBlock
              label="Основание отклонения"
              labelFontSize="small"
              button={
                <Button
                  className="shared-btn-add"
                  text="Добавить"
                  variant="link"
                  color="primary"
                  type="button"
                  onClick={addRejectReason}
                />
              }
            ></FieldBlock>
          </Row>
          <FieldArray name="rejectReasons">
            {({ fields }) =>
              fields.map((name, index) => {
                const typeSelectValue = fields.value?.[index]?.type;
                const filteredKnmRejectReasonTypesOptions: IKnmRejectReasonTypeSelectOption[] =
                  knmRejectReasonTypesOptions?.map(knmRejectReasonTypesOption => ({
                    ...knmRejectReasonTypesOption,
                    isDisabled: fields.value?.some(
                      rejectReason =>
                        rejectReason?.type?.code === knmRejectReasonTypesOption.code &&
                        knmRejectReasonTypesOption.code !== typeSelectValue?.code,
                    ),
                  })) || [];

                return (
                  <Row key={name} notWrap>
                    <Row margin="none">
                      <FieldBlock>
                        <SelectInputField
                          name={`${name}.type`}
                          options={filteredKnmRejectReasonTypesOptions}
                          size="medium"
                          validate={makeRequired('Поле не может быть пустым')}
                        />
                      </FieldBlock>
                      {fields.value?.[index]?.type?.hasText && (
                        <FieldBlock>
                          <TextareaField
                            name={`${name}.note`}
                            placeholder="Иное основание отклонения"
                            validate={makeRequired('Поле не может быть пустым')}
                          />
                        </FieldBlock>
                      )}
                    </Row>
                    <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveRejectReasonHandler(index)} />
                  </Row>
                );
              })
            }
          </FieldArray>
        </form>
      );
    },
    [knmRejectReasonTypesOptions],
  );

  const handleFormSubmit = useCallback(() => {
    const formRejectReasons = formRef.current?.getState()?.values?.rejectReasons;
    const rejectReasons = formRejectReasons?.reduce(
      (filteredRejectReasons: IDisapproveRejectReason[] | undefined, formRejectReason) => {
        if (formRejectReason?.type) {
          const res = filteredRejectReasons ?? [];

          res.push({
            type: deleteObjectProperties(formRejectReason.type, ['label', 'value']),
            note: formRejectReason.note,
          });

          return res;
        }

        return filteredRejectReasons;
      },
      undefined,
    );

    if (rejectReasons?.length) {
      onApply(rejectReasons);
    }
  }, [onApply]);

  return (
    <Modal visible={visible} modalBodyClassName={styles.ModalBody} onClose={onClose} afterClose={handleAfterClose}>
      <ModalHeader title={`Отклонение КНМ (${knmsNumber})`} sizeText="small" />
      <ModalBody size="content" border="bottom">
        <Form<IDisapproveKnmsForm> render={renderForm} onSubmit={handleFormSubmit} mutators={{ ...arrayMutators }} />
      </ModalBody>
      <ModalActions>
        <Button text="Отклонить" color="primary" size="medium" type="button" onClick={handleApplyButtonClick} />
        <Button text="Отменить" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
